import { Button, Chip, Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import ModuleContext from 'CMFW/context/ModuleContext';
import TextInput from 'CMFW/input/TextInput';
import { Separator } from 'CMFW/layout/Separator';
import { EditorGapGreedy, EditorRow } from 'components/common/styles';
import HanziModel from 'models/HanziModel';
import React, { useContext, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { CharBadge, HanziGlyph, pathLength } from './HanziGlyph';
import { CardItem, MissingItem, PIECE_COLORS, Piece, ROLE_COLORS, numFromCardId } from './types';

/** Item pane of every Hanzi tab: the card, read-only. Placed inside <ItemLayout>. */
const HanziCardItem: React.FC = () => {
  const model = useContext(ModuleContext) as HanziModel;
  const item = model.itemUpdated as CardItem | MissingItem | undefined;
  const [highlight, setHighlight] = useState<number | null>(null);
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const raf = useRef<number | null>(null);
  const cardId = item && !item.missing ? item.cardId : null;

  // Stroke-by-stroke animation: each stroke takes a time proportional to its length, with a short pause after it.
  const stop = () => {
    if (raf.current !== null) {
      cancelAnimationFrame(raf.current);
      raf.current = null;
    }
    setProgress(undefined);
  };
  const play = () => {
    if (!item || item.missing) {
      return;
    }
    stop();
    const strokes = item.strokes;
    const durations = strokes.map((s) => 250 + pathLength(s.median) * 0.6);
    const pause = 180;
    const start = performance.now();
    const tick = (now: number) => {
      let t = now - start;
      let i = 0;
      while (i < strokes.length && t >= durations[i] + pause) {
        t -= durations[i] + pause;
        i += 1;
      }
      if (i >= strokes.length) {
        setProgress(strokes.length);
        raf.current = null;
        return;
      }
      setProgress(i + Math.min(1, t / durations[i]));
      raf.current = requestAnimationFrame(tick);
    };
    setProgress(0);
    raf.current = requestAnimationFrame(tick);
  };
  useEffect(() => {
    stop();
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardId]);

  if (!item) {
    return null;
  }
  if (item.missing) {
    return (
      <EditorRow>
        <span>No card for {item.cardId}. Create it from the Wizard tab.</span>
      </EditorRow>
    );
  }
  const card = item;
  // The card is always shown down to the components of the norm; the intermediate
  // decomposition of the sources is kept in the database but only shown on demand.
  const view = card.base.expanded
    ? { strokes: card.strokes.map((s, i) => ({ ...s, piece: card.base.strokePieces[i] })), pieces: card.base.pieces }
    : { strokes: card.strokes, pieces: card.pieces };
  const pieces: Piece[] = view.pieces;
  const open = (id: string) => model.loadItemEdit(numFromCardId(id));

  return (
    <Container>
      <EditorRow>
        <HanziGlyph
          card={view}
          size={200}
          colorMode="piece"
          highlightPieces={highlight === null ? null : [highlight]}
          numbers={model.showOrder}
          progress={progress}
        />
        <BigChar>{card.chr ?? card.cardId}</BigChar>
        <EditorGapGreedy />
        <Buttons>
          <Button variant="outlined" size="small" onClick={raf.current !== null ? stop : play}>
            {raf.current !== null ? 'stop' : 'play strokes'}
          </Button>
          <Button variant="outlined" size="small" onClick={() => model.toggleOrder()}>
            {model.showOrder ? 'hide order' : 'show order'}
          </Button>
          <Button variant="outlined" size="small" onClick={() => model.rebuild()}>
            rebuild
          </Button>
        </Buttons>
      </EditorRow>
      <Scroll>
        <EditorRow>
          <Narrow>
            <TextInput keyName={'cardId'} label={'Id'} disabled />
          </Narrow>
          <Wide>
            <TextInput keyName={'gloss'} label={'Gloss (internal, EN)'} disabled />
          </Wide>
          <Medium>
            <TextInput keyName={'internalName'} label={'Internal name'} disabled />
          </Medium>
        </EditorRow>
        <EditorRow>
          <Medium>
            <TextInput keyName={'structureLabel'} label={'Structure'} disabled />
          </Medium>
          <Medium>
            <TextInput keyName={'ids'} label={'IDS'} disabled />
          </Medium>
          <Narrow>
            <TextInput keyName={'strokeCount'} label={'Strokes'} disabled />
          </Narrow>
          <Medium>
            <TextInput keyName={'radicalLabel'} label={'Radical'} disabled />
          </Medium>
        </EditorRow>
        <EditorRow>
          <Medium>
            <TextInput keyName={'typeLabel'} label={'Type'} disabled />
          </Medium>
          <Medium>
            <TextInput keyName={'gfLabel'} label={'GF 0014-2009'} disabled />
          </Medium>
          <Wide>
            <TextInput keyName={'flags'} label={'Flags'} disabled />
          </Wide>
        </EditorRow>
        <EditorRow>
          <Medium>
            <TextInput keyName={'origin'} label={'Origin'} disabled />
          </Medium>
          <Wide>
            <TextInput keyName={'drawnFrom'} label={'Drawn from'} disabled />
          </Wide>
        </EditorRow>
        {pieces.length > 0 && (
          <>
            <Separator />
            <EditorRow>
              <Title>Components (GF 0014-2009)</Title>
            </EditorRow>
            <Table size="small">
              <TableBody>
                {pieces.map((p) => {
                  const strokes = view.strokes.filter((s) => s.piece === p.idx).map((s) => s.order);
                  return (
                    <TableRow
                      key={p.idx}
                      hover
                      onMouseEnter={() => setHighlight(p.idx)}
                      onMouseLeave={() => setHighlight(null)}
                      onClick={() => p.childId && open(p.childId)}
                      style={{ cursor: p.childId ? 'pointer' : 'default' }}
                    >
                      <TableCell padding="none">
                        <Dot style={{ background: PIECE_COLORS[p.idx % PIECE_COLORS.length] }} />
                      </TableCell>
                      <TableCell padding="none">
                        <CharBadge chr={p.child?.chr ?? null} id={p.childId ?? '？'} />
                      </TableCell>
                      <TableCell>
                        {p.child && p.child.gfSeq !== null ? (
                          <>
                            {p.child.internalName ?? p.child.gfName}
                            <Muted>
                              {' '}
                              · GF {p.child.gfSeq}
                              {p.child.internalName && p.child.gfName ? ` ${p.child.gfName}` : ''}
                            </Muted>
                          </>
                        ) : (
                          <>
                            {p.child?.internalName ?? p.child?.gloss ?? 'unresolved'}
                            {p.child && <Muted> · not a component of the norm</Muted>}
                          </>
                        )}
                      </TableCell>
                      <TableCell>
                        <Muted>{p.position ?? '—'}</Muted>
                        {p.via && p.via.length > 0 && <Muted> · via {p.via.join(' › ')}</Muted>}
                      </TableCell>
                      <TableCell style={{ color: ROLE_COLORS[p.role] }}>{p.role !== 'none' ? p.role : ''}</TableCell>
                      <TableCell>
                        <Muted>{strokes.length ? strokes.join(',') : 'no strokes'}</Muted>
                      </TableCell>
                      <TableCell>{p.resolvedByGf && <Chip size="small" label="GF" />}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            {view.strokes.some((s) => s.piece === null) && (
              <EditorRow>
                <Muted>
                  Strokes without piece:{' '}
                  {view.strokes
                    .filter((s) => s.piece === null)
                    .map((s) => s.order)
                    .join(', ')}
                </Muted>
              </EditorRow>
            )}
          </>
        )}
        <Separator />
        <EditorRow>
          <Title>Used in ({card.usedBy})</Title>
        </EditorRow>
        <Chips>
          {card.usedIn.length === 0 && <Muted>—</Muted>}
          {card.usedIn.map((u) => (
            <Chip
              key={u}
              size="small"
              variant="outlined"
              clickable
              onClick={() => open(u)}
              label={u.startsWith('gf-') ? u : u}
            />
          ))}
          {card.usedBy > card.usedIn.length && <Muted>… and {card.usedBy - card.usedIn.length} more</Muted>}
        </Chips>
        {card.base.expanded && (
          <>
            <Separator />
            <EditorRow>
              <Button variant="text" size="small" onClick={() => model.toggleFlat()}>
                {model.flat ? 'show' : 'hide'} source decomposition (internal, not taught)
              </Button>
            </EditorRow>
            {!model.flat && (
              <Table size="small">
                <TableBody>
                  {card.pieces.map((p) => (
                    <TableRow
                      key={p.idx}
                      hover
                      onClick={() => p.childId && open(p.childId)}
                      style={{ cursor: p.childId ? 'pointer' : 'default' }}
                    >
                      <TableCell padding="none">
                        <CharBadge chr={p.child?.chr ?? null} id={p.childId ?? '？'} />
                      </TableCell>
                      <TableCell>{p.child?.gloss ?? p.child?.internalName ?? 'unresolved'}</TableCell>
                      <TableCell>
                        <Muted>{p.position ?? '—'}</Muted>
                      </TableCell>
                      <TableCell style={{ color: ROLE_COLORS[p.role] }}>{p.role !== 'none' ? p.role : ''}</TableCell>
                      <TableCell>
                        <Muted>
                          {card.strokes
                            .filter((s) => s.piece === p.idx)
                            .map((s) => s.order)
                            .join(',') || 'no strokes'}
                        </Muted>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </>
        )}
      </Scroll>
    </Container>
  );
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const Scroll = styled.div`
  flex-grow: 1;
  overflow: auto;
  padding-bottom: 10px;
  padding-right: 10px;
`;

const BigChar = styled.div`
  font-size: 48px;
  line-height: 1;
  font-family: 'Noto Sans SC', 'PingFang SC', 'Microsoft YaHei', sans-serif;
`;

const Buttons = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: stretch;
`;

const Narrow = styled.div`
  flex: 1;
`;

const Medium = styled.div`
  flex: 2;
`;

const Wide = styled.div`
  flex: 4;
`;

const Title = styled.span`
  font-weight: bold;
`;

const Muted = styled.span`
  color: #757575;
`;

const Dot = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 5px;
  margin-left: 6px;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin: 0 10px;
`;

export default HanziCardItem;
